import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { Notice } from "../../design/components";
import { age, dateTime, omr } from "../../lib/format";
import type { HeldRow } from "../../api/routing";
import styles from "./inbound.module.css";

/** The receiver identifier is `<scheme>:<cr>`; an unknown CR is named on its own. */
export function reasonSentence(row: HeldRow): string {
  const receiver = row.receiverParticipant;
  const t = i18n.t.bind(i18n);
  if (row.reason === "no_receiver_identifier" || !receiver) {
    return t("inbound.held.reasonNoReceiver", "The document names no receiver identifier.");
  }
  if (row.reason === "unknown_cr") {
    const colon = receiver.indexOf(":");
    const cr = colon < 0 ? receiver : receiver.slice(colon + 1);
    return t("inbound.held.reasonUnknownCr", "CR {{cr}} does not match any company in the group.", { cr });
  }
  return t(
    "inbound.held.reasonUnknownParticipant",
    "Participant {{receiver}} does not match any company in the group.",
    { receiver },
  );
}

export function HeldTable({
  rows,
  isLoading,
  onAssign,
}: {
  rows: HeldRow[];
  isLoading: boolean;
  onAssign: (row: HeldRow) => void;
}) {
  const { t } = useTranslation();
  if (!rows.length && !isLoading) {
    return (
      <div className="card-body">
        <Notice kind="info">
          {t("inbound.held.empty", "Nothing held. Every received document has routed to a company.")}
        </Notice>
      </div>
    );
  }
  return (
    <div className="table-wrap">
      <table className="tbl" aria-label={t("inbound.held.tableLabel", "Held")}>
        <thead>
          <tr>
            <th className="nowrap">{t("inbound.held.colReceived", "Received")}</th>
            <th>{t("inbound.held.colSupplier", "Supplier")}</th>
            <th>{t("inbound.held.colInvoiceNumber", "Invoice number")}</th>
            <th>{t("inbound.held.colReceiverIdentifier", "Receiver identifier")}</th>
            <th>{t("inbound.held.colReason", "Reason")}</th>
            <th className={styles.suggested}>{t("inbound.held.colSuggested", "Suggested")}</th>
            <th className="r">{t("inbound.held.colAge", "Age")}</th>
            <th aria-label={t("inbound.held.colAssign", "Assign")} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.holdId}>
              <td className="nowrap">{dateTime(row.receivedAt)}</td>
              <td>{row.sellerName ?? "—"}</td>
              <td>
                <Link className="link" to={`/inbound/${row.documentId}`}>
                  {row.invoiceNumber ?? row.documentId}
                </Link>
                <div className={styles.subtle}>
                  {omr(row.total)}
                  {row.currency ? ` ${row.currency}` : ""}
                </div>
              </td>
              <td className="mono nowrap">{row.receiverParticipant ?? "—"}</td>
              <td className={styles.reason}>{reasonSentence(row)}</td>
              <td className={styles.suggested}>{row.candidateEntityName ?? "—"}</td>
              <td className="r nowrap">{age(row.receivedAt)}</td>
              <td className="r nowrap">
                <button type="button" className="btn btn-sm" onClick={() => onAssign(row)}>
                  {t("inbound.held.assignButton", "Assign to company")}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
